import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '../../../services/api';

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'available' ? '#2e7d3215' :
    $status === 'assigned' ? '#0291ef15' :
    $status === 'maintenance' ? '#ed6c0215' :
    '#9e9e9e15'};
  color: ${({ $status }) =>
    $status === 'available' ? '#2e7d32' :
    $status === 'assigned' ? '#0291ef' :
    $status === 'maintenance' ? '#ed6c02' :
    '#9e9e9e'};
`;

const useAssetApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const lastValidQueryParams = useRef(null);

  // Fetch categories and locations on mount
  useEffect(() => {
    api.get('/v1/asset-categories?pageSize=1000').then((res) => {
      setCategories(res.data?.data?.data || []);
    }).catch(() => {});
    api.get('/v1/locations?pageSize=1000').then((res) => {
      setLocations(res.data?.data?.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/assets?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/assets', formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/assets/${id}`, formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/assets/${ids[0]}`);
      } else {
        await api.post('/v1/assets/batch-delete', ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const statusLabels = useMemo(() => ({
    available: t('asset.available'),
    assigned: t('asset.assigned'),
    maintenance: t('asset.maintenance'),
    retired: t('asset.retired'),
  }), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('asset.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'categoryName', label: t('asset.categoryName'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'brand', label: t('asset.brand'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'model', label: t('asset.model'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'serialNumber', label: t('asset.serialNumber'), options: { filter: true, sort: false, display: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'barcodeNumber', label: t('asset.barcodeNumber'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'status', label: t('asset.status'),
      options: {
        filter: true, sort: true,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: [t('asset.available'), t('asset.assigned'), t('asset.maintenance'), t('asset.retired')],
          renderValue: (v) => v,
        },
        filterList: [],
        customFilterListOptions: { render: (v) => v },
        customBodyRender: (v) => <StatusBadge $status={v}>{statusLabels[v] || v}</StatusBadge>,
      },
    },
    { name: 'supplier', label: t('asset.supplier'), options: { filter: true, sort: false, display: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'locationName', label: t('asset.locationName'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
  ], [t, statusLabels]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('asset.name'), type: 'text', grid: 12, required: true },
    {
      name: 'assetCategoryId', label: t('asset.categoryName'), type: 'select', grid: 6, required: true,
      options: categories, idKey: 'id', labelKey: 'name',
    },
    {
      name: 'locationId', label: t('asset.locationName'), type: 'select', grid: 6,
      options: locations, idKey: 'id', labelKey: 'name',
    },
    { name: 'brand', label: t('asset.brand'), type: 'text', grid: 6 },
    { name: 'model', label: t('asset.model'), type: 'text', grid: 6 },
    { name: 'serialNumber', label: t('asset.serialNumber'), type: 'text', grid: 6 },
    { name: 'barcodeNumber', label: t('asset.barcodeNumber'), type: 'text', grid: 6 },
    { name: 'supplier', label: t('asset.supplier'), type: 'text', grid: 12 },
    { name: 'purchaseDate', label: t('asset.purchaseDate'), type: 'date', grid: 4 },
    { name: 'warrantyEndDate', label: t('asset.warrantyEndDate'), type: 'date', grid: 4 },
    { name: 'maintenanceEndDate', label: t('asset.maintenanceEndDate'), type: 'date', grid: 4 },
    { name: 'description', label: t('asset.description'), type: 'textarea', grid: 12 },
  ], [t, categories, locations]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

export default useAssetApi;
